/**
 * The route table.
 *
 * One file, so the whole navigable surface of the application can be read at once
 * — including the boundary that matters most:
 *
 *   `/register`  is public. It renders outside `AppLayout` and calls exactly one
 *                endpoint (Section 1.7).
 *   everything else is staff-facing and renders inside `AppLayout`, which is where
 *                Parinaam's authentication guard will eventually be added.
 *
 * Adding a staff screen means adding a child of the layout route. Adding a second
 * public page means a conversation, not a commit.
 */

import { Route, Routes } from 'react-router-dom';
import { AppLayout } from '../layouts/AppLayout';
import { DashboardPage } from '../pages/dashboard/DashboardPage';
import { RegistrationsPage } from '../pages/registration/RegistrationsPage';
import { RegistrationReviewPage } from '../pages/registration/RegistrationReviewPage';
import { PublicRegistrationPage } from '../pages/registration/PublicRegistrationPage';
import { VolunteersPage } from '../pages/volunteers/VolunteersPage';
import { VolunteerProfilePage } from '../pages/volunteers/VolunteerProfilePage';
import { ProgramsPage } from '../pages/programs/ProgramsPage';
import { ProgramDetailPage } from '../pages/programs/ProgramDetailPage';
import { ActivitiesPage } from '../pages/activities/ActivitiesPage';
import { ActivityDetailPage } from '../pages/activities/ActivityDetailPage';
import { AttendancePage } from '../pages/attendance/AttendancePage';
import { DataToolsPage } from '../pages/admin/DataToolsPage';
import { ExportsPage } from '../pages/admin/ExportsPage';
import { CertificatesPage } from '../pages/admin/CertificatesPage';
import { NotFoundPage } from '../pages/NotFoundPage';
import { SignInPage } from '../pages/auth/SignInPage';
import { AccountsPage } from '../pages/admin/AccountsPage';
import { SessionDetailPage } from '../pages/activities/SessionDetailPage';
import { ProfilePage } from '../pages/profile/ProfilePage';
import { ForcedPasswordChange } from '../pages/profile/ForcedPasswordChange';
import { useAuth } from '../hooks/useAuth';
import { RequireStaff } from './RequireStaff';
import { RequireUser } from './RequireUser';
import { RequireVolunteer } from './RequireVolunteer';
import { MySessionsPage } from '../pages/volunteer/MySessionsPage';
import { MyHoursPage } from '../pages/volunteer/MyHoursPage';
import { OpportunitiesPage } from '../pages/volunteer/OpportunitiesPage';
import { OpportunityDetailPage } from '../pages/volunteer/OpportunityDetailPage';

export function AppRoutes() {
  const { user } = useAuth();

  /*
   * A password that must be replaced takes over the whole application.
   *
   * Before <Routes>, not inside it, and that is deliberate: a route-level
   * guard would have to be remembered on every route added afterwards, and
   * the one somebody forgets is the hole. Returning early here means there is
   * no route to forget — /register and /login are below it because a person
   * in this state is by definition already signed in.
   *
   * The API enforces the same thing independently (requirePasswordCurrent).
   * This is what turns that 403 into a screen that says what to do.
   *
   * Optional-chained through `password` as well as `user`: a portal served
   * against an API that predates this feature gets no such field, and a blank
   * white screen is a worse answer than not enforcing a rule that server does
   * not have.
   */
  if (user?.password?.must_change) return <ForcedPasswordChange />;

  return (
    <Routes>
      {/* Public - outside the staff shell. */}
      <Route path="/register" element={<PublicRegistrationPage />} />
      <Route path="/login" element={<SignInPage />} />

      {/*
        Your own account — every role, one declaration.

        ABOVE the two role blocks, and that position is the fix for a real
        bug rather than a tidy-up. It was declared inside both of them, on
        the reasoning that a page about your own account has nobody who does
        not belong. But React Router matches the first declaration and stops:
        every account was handed to `RequireVolunteer`, and staff failed its
        role check and were redirected to the dashboard. Declaring a path
        twice does not mean "either guard applies" — it means the one written
        first applies to everybody.

        `RequireUser` asks only whether somebody is signed in, which is the
        whole question here.
      */}
      <Route
        element={
          <RequireUser>
            <AppLayout />
          </RequireUser>
        }
      >
        <Route path="/profile" element={<ProfilePage />} />
      </Route>

      {/*
        The volunteer's routes, in the SAME shell as the staff ones.

        Both guards wrap `AppLayout`; the layout picks its navigation from the
        role. One product that looks the same whoever signs in, rather than two
        that have to be kept in step — which is what the second one always
        stops being.

        Their index is "/" as well, so signing in lands everybody somewhere
        useful without the URL depending on who they are.
      */}
      <Route
        element={
          <RequireVolunteer>
            <AppLayout />
          </RequireVolunteer>
        }
      >
        <Route path="/my-volunteering" element={<MySessionsPage />} />
        <Route path="/opportunities" element={<OpportunitiesPage />} />
        {/*
          The detail behind a card on that list.

          Under /opportunities rather than off /sessions, which is the staff
          screen: same subject, different reader. A volunteer's URL must never
          be one that lands them on a page they cannot load.
        */}
        <Route path="/opportunities/:id" element={<OpportunityDetailPage />} />
        <Route path="/my-hours" element={<MyHoursPage />} />
      </Route>

      {/*
        Staff portal. The guard wraps the layout rather than each page, so a
        route added below cannot quietly arrive unguarded - the same reason the
        API mounts its role check on the router.
      */}
      <Route
        element={
          <RequireStaff>
            <AppLayout />
          </RequireStaff>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="registrations" element={<RegistrationsPage />} />
        <Route path="registrations/:id" element={<RegistrationReviewPage />} />
        <Route path="volunteers" element={<VolunteersPage />} />
        <Route path="volunteers/:id" element={<VolunteerProfilePage />} />
        <Route path="programs" element={<ProgramsPage />} />
        <Route path="programs/:id" element={<ProgramDetailPage />} />
        <Route path="activities" element={<ActivitiesPage />} />
        <Route path="activities/:id" element={<ActivityDetailPage />} />
        {/*
          Off /sessions rather than nested under the activity: a session has one
          identity and one URL, and nesting would make the same screen reachable
          by two paths that have to be kept in step.
        */}
        <Route path="sessions/:id" element={<SessionDetailPage />} />
        <Route path="attendance" element={<AttendancePage />} />
        {/*
          ADMIN-only in practice, but guarded inside the page rather than by a
          route wrapper. The page has to ask the API whether the operation
          exists on this installation anyway, and a second gate at the route
          would answer that question in two places - which is how they come to
          disagree. A TEAM account that types the URL gets a plain "admins only"
          rather than a 404 pretending the screen is not there.
        */}
        <Route path="admin/data" element={<DataToolsPage />} />
        <Route path="admin/export" element={<ExportsPage />} />
        <Route path="admin/accounts" element={<AccountsPage />} />
        <Route path="admin/certificates" element={<CertificatesPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
